import _ from "lodash";
import store from 'services/redux/store'
import { setData } from 'services/redux/data/actions'
import { dataConstants } from 'services/redux/data/constants'
import FetchCache from "utils/fetch/cached/FetchCache";
import CacheStorage from 'utils/cache/CacheStorage'
import CacheByUpdatedAt from 'utils/cache/CacheByUpdatedAt'
import FetchMessage from "utils/fetch/FetchMessage";
import { messageKinds } from 'utils/constants/message-constants'
import HelperObject from 'utils/helper-object'
import ObjectMessage from 'utils/objects/message'
import { constantsActive, constantsGraphql } from 'utils/constants/constants'
import { calculatePagination, createPagination } from 'utils/helper-pagination'

export default class FetchCachedMessage
{
	static logName = 'FetchCachedMessage';
	static values = null;
	static cacheUpdatedAt = null;
	static cacheScope = null;
	static snapshotUpdatedAt = null;
	static reloadVersion = 0;

	static variables = { active: constantsGraphql.YES }
	static kinds = [
		messageKinds.INVITATION,
		//messageKinds.INVITATION_UPDATE,
		messageKinds.COMPANY_MESSAGE,
		messageKinds.MOD_MESSAGE,
		messageKinds.UNKNOWN
	]
	static paginationItems = 20
	static pagination = createPagination( this.paginationItems, 1 )

	static construct = () => {
		return (
			FetchCache.initialize( this, {
				identifier: 'messages',
				tables: [ 'my_messages' ],
				onExpire: this.reload,
				updated_at_max_age: ( 30 )
			}, this.hydrate )
		);
	};

	static scope = ( variables = this.variables ) => {
		return JSON.stringify(variables);
	};

	static hydrate = snapshot => {
		if( !snapshot || !snapshot.messages || !snapshot.variables ) return;
		this.cacheScope = this.scope( snapshot.variables );
		this.snapshotUpdatedAt = snapshot.updatedAt ?? CacheByUpdatedAt.defaultDatetime;
		this.pagination = snapshot.pagination ?? createPagination( this.paginationItems, 1 );
		this.process( snapshot.messages, { replace: true } );
	}

	static persist = ( variables, registerUpdates ) => {
		this.cacheScope = this.scope( variables );
		if( registerUpdates ) {
			this.snapshotUpdatedAt = CacheByUpdatedAt.getTableUpdatedAt( 'my_messages' );
			CacheByUpdatedAt.registerAsUpdates({ tables: this.cacheUpdatedAt.tables, updated_at: this.snapshotUpdatedAt });
		}
		// Loading one more page must not make older pages appear newly refreshed.
		CacheStorage.storeData( this.cacheUpdatedAt.identifier, {
			messages: this.values,
			variables,
			pagination: this.pagination,
			updatedAt: this.snapshotUpdatedAt,
		}, { updated_at: this.snapshotUpdatedAt ?? CacheByUpdatedAt.defaultDatetime } );
	}

	static fetchAll = async ( callbacks = {} ) => {
		await this.construct();
		if( this.values === null || this.cacheScope !== this.scope() ) return this.reload( callbacks );
		return FetchCache.sniff( this.cacheUpdatedAt, {
			onFresh: () => {
				return callbacks.callbackSuccess?.({
					messages: this.values
				});
			},
			onExpired: () => {
				return this.reload(callbacks);
			},
		});
	}

	static fetchMore = async ( { callbackSuccess = null, callbackError = null, callbackStart = null } = {} ) => {
		await this.construct();
		const variables = { ...this.variables };
		if( this.cacheScope !== this.scope( variables ) ) this.pagination = createPagination( this.paginationItems, 1 );
		const pagination = { ...this.pagination };
		const version = this.reloadVersion;
		return this.construct().then( () =>
			{
			return (
				FetchMessage.fetchAll({
					kind: this.kinds,
					pagination,
					variables,
					callbackStart: callbackStart,
					callbackError: callbackError,
					callbackSuccess: ( { messages = null } ) => {

						if( version !== this.reloadVersion || this.scope( variables ) !== this.scope() ) {
							callbackSuccess?.({ messages: this.values });
							return;
						}
						this.process( messages )

						if( _.size( messages ) === this.paginationItems ) {
							this.createPagination( pagination.page )
						}

						this.persist( variables, false );

						if( _.isFunction( callbackSuccess ) ) {
							callbackSuccess({
								messages: messages
							});
						}
					}
				})
			);
		}
		);
	}

	static reload = async ( { callbackSuccess = null, callbackError = null, callbackStart = null } = {} ) => {
		const variables = { ...this.variables };
		const version = ++this.reloadVersion;
		await this.construct();
		let pagination = createPagination( calculatePagination( this.paginationItems, Math.max( 1, this.pagination.page - 1 ) ), 1 )

		// Active messages are fetched as a complete list.
		if( variables.active === constantsGraphql.YES ) {
			pagination = {}
			this.pagination = createPagination( this.paginationItems, 1 );
		}

		return this.construct().then( () =>
			{
			return (
				FetchMessage.fetchAll({
					kind: this.kinds,
					pagination: pagination,
					variables,
					callbackStart: callbackStart,
					callbackError: callbackError,
					callbackSuccess: ( { messages = null } ) => {

						if( version !== this.reloadVersion || this.scope( variables ) !== this.scope() ) {
							callbackSuccess?.({ messages: this.values });
							return;
						}
						this.process( messages, { replace: true } )
						if( variables.active !== constantsGraphql.YES ) {
							this.pagination = createPagination( this.paginationItems, Math.floor( _.size( messages ) / this.paginationItems ) + 1 );
						}

						// Save to cache!
						this.persist( variables, true );

						if( _.isFunction( callbackSuccess ) ) {
							callbackSuccess({
								messages: messages
							});
						}
					}
				})
			);
		}
		);
	}

	static createPagination = ( currentPage ) => {
		this.pagination = createPagination( this.paginationItems, currentPage + 1 )
	}

	static process = ( messages, { replace = false } = {} ) => {
		messages = HelperObject.groupByGuid( ObjectMessage.order( messages ) );

		this.values = {
			...(replace ? {} : this.values),
			...messages
		};

		store.dispatch( setData( dataConstants.MESSAGES, this.values ) );

		const messageCount = _.countBy( this.values, {
			is_read: constantsActive.NO,
			active: constantsActive.YES
		});

		store.dispatch( setData( dataConstants.MESSAGE_COUNT, _.get( messageCount, 'true', 0 ) ) );
	}
}
